/**
 * useKeyboardHeight — returns the on-screen keyboard height in CSS pixels.
 *
 * Uses the visualViewport API (iOS Safari 13+, Chrome 100+, Firefox 91+).
 * Returns 0 when:
 *   - the API is unavailable (older browsers, SSR)
 *   - the keyboard is closed
 *
 * iOS Safari behavior: when the keyboard appears, `visualViewport.height`
 * shrinks by the keyboard's height AND `visualViewport.offsetTop` grows by
 * the same amount (the page is "pushed up" so the focused input stays
 * visible). We compute keyboard height as:
 *
 *   innerHeight - visualViewport.height - visualViewport.offsetTop
 *
 * That math gives 0 when the keyboard is closed and the keyboard height
 * (positive) when it is open, regardless of scroll position.
 *
 * Caveats:
 * - iOS sometimes returns intermediate values during the keyboard
 *   animation. The hook debounces naturally via React batching.
 * - Android Chrome has visualViewport too but the keyboard doesn't always
 *   shrink the viewport (depends on `interactive-widget` viewport meta).
 *   We still get a reasonable estimate on Android.
 */
import { useEffect, useState } from 'react';

export function useKeyboardHeight(): number {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const vv = window.visualViewport;

    const update = () => {
      // Inner height = layout viewport (full window height).
      // visualViewport.height = visible portion (excluding keyboard).
      // visualViewport.offsetTop = how far the visual viewport has been
      //   pushed up by iOS to keep the focused input visible.
      // Keyboard occupies: innerHeight - visible - offsetTop
      const height = Math.max(
        0,
        window.innerHeight - vv.height - (vv.offsetTop || 0),
      );
      setKeyboardHeight(height);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return keyboardHeight;
}
